'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import RichText from '@/components/ui/RichText/RichText';
import type { CarouselDoctor } from '@/utils/getDoctorsByCategory';

import SpecialistCarouselCard from './SpecialistCarouselCard';

interface Props {
  doctors: CarouselDoctor[];
  heading?: string;
}

const SpecialistCarouselSlider = ({
  doctors: sourceDoctors,
  heading,
}: Props) => {
  const itemsRef = useRef<(HTMLDivElement | null)[]>([]);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const touchStartRef = useRef({ x: 0, y: 0 });
  const swipeLockedRef = useRef(false);
  const animFrameRef = useRef<number | null>(null);

  const [activeIndex, setActiveIndex] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);
  const doctors = sourceDoctors;

  const smoothScrollTo = useCallback(
    (scroller: HTMLDivElement, target: number) => {
      if (animFrameRef.current !== null)
        cancelAnimationFrame(animFrameRef.current);

      const start = scroller.scrollLeft;
      const distance = target - start;
      if (Math.abs(distance) < 1) return;

      const duration = 380;
      const startTime = performance.now();

      const step = (now: number) => {
        const t = Math.min((now - startTime) / duration, 1);
        const ease = t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
        scroller.scrollLeft = start + distance * ease;
        if (t < 1) animFrameRef.current = requestAnimationFrame(step);
        else animFrameRef.current = null;
      };

      animFrameRef.current = requestAnimationFrame(step);
    },
    [],
  );

  useEffect(() => {
    return () => {
      if (animFrameRef.current !== null)
        cancelAnimationFrame(animFrameRef.current);
    };
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    const check = () => {
      const scroller = scrollerRef.current;
      if (!scroller) return;
      setIsScrollable(scroller.scrollWidth > scroller.clientWidth);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [doctors.length]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    const el = itemsRef.current[activeIndex];
    if (!scroller || !el) return;

    const scrollerRect = scroller.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const delta =
      elRect.left +
      elRect.width / 2 -
      (scrollerRect.left + scrollerRect.width / 2);

    requestAnimationFrame(() => {
      smoothScrollTo(scroller, scroller.scrollLeft + delta);
    });
  }, [activeIndex, smoothScrollTo]);

  const onPrev = useCallback(
    () => setActiveIndex((i) => (i > 0 ? i - 1 : i)),
    [],
  );
  const onNext = useCallback(
    () => setActiveIndex((i) => (i < doctors.length - 1 ? i + 1 : i)),
    [doctors.length],
  );

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    const t = e.touches[0];
    if (!t) return;
    touchStartRef.current = { x: t.clientX, y: t.clientY };
    swipeLockedRef.current = false;
  }, []);

  const onTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (swipeLockedRef.current) return;
      const t = e.touches[0];
      if (!t) return;
      const dx = t.clientX - touchStartRef.current.x;
      const dy = t.clientY - touchStartRef.current.y;
      if (Math.abs(dy) > Math.abs(dx) || Math.abs(dx) < 35) return;
      swipeLockedRef.current = true;
      setActiveIndex((i) => {
        if (dx < 0) return i < doctors.length - 1 ? i + 1 : i;
        return i > 0 ? i - 1 : i;
      });
    },
    [doctors.length],
  );

  const onTouchEnd = useCallback(() => {
    swipeLockedRef.current = false;
  }, []);

  return (
    <div className="w-full">
      {heading && (
        <RichText
          styles={{
            p: 'text-2xl md:text-3xl font-bold text-theme-primary mb-6',
          }}
        >
          {heading}
        </RichText>
      )}

      <div className="overflow-x-hidden">
        <div
          ref={scrollerRef}
          className="overflow-x-hidden py-3"
          style={{ touchAction: 'pan-y' }}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div className="flex w-max items-stretch gap-4 px-4">
            {doctors.map((doctor, idx) => {
              const isActive = idx === activeIndex;
              const disableOnMobile = isMobile && !isActive;
              const scale = !isScrollable
                ? hoveredIndex === idx
                  ? 'scale-[1.05] z-10'
                  : ''
                : isActive
                  ? hoveredIndex !== null && hoveredIndex !== idx
                    ? 'scale-[0.95] z-10'
                    : 'scale-[1.05] z-10'
                  : hoveredIndex === idx
                    ? 'scale-[1.05] z-10'
                    : 'scale-[0.95]';

              return (
                <div
                  key={doctor.slug}
                  ref={(el) => {
                    itemsRef.current[idx] = el;
                  }}
                  onMouseEnter={() => setHoveredIndex(idx)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  tabIndex={disableOnMobile ? -1 : 0}
                  className={`relative transition-transform duration-300 ease-out will-change-transform focus:outline-none ${scale} ${
                    disableOnMobile ? 'pointer-events-none' : 'cursor-pointer'
                  }`}
                >
                  <SpecialistCarouselCard doctor={doctor} />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {isScrollable && (
        <div className="mt-6 flex justify-center gap-3">
          <button
            type="button"
            onClick={onPrev}
            disabled={activeIndex === 0}
            aria-label="Previous"
            className="border-gray-150 flex h-12 w-12 cursor-pointer items-center justify-center rounded-full border bg-white text-slate-700 shadow-[0_2px_8px_rgba(0,0,0,0.08)] transition-all duration-200 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <svg
              className="text-text-main h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 19.5L8.25 12l7.5-7.5"
              />
            </svg>
          </button>
          <div className="flex items-center gap-1.5">
            {doctors.map((_, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => setActiveIndex(idx)}
                aria-label={`Go to slide ${idx + 1}`}
                className={`rounded-full transition-all duration-300 ${
                  idx === activeIndex
                    ? 'bg-theme-brand-blue h-2 w-4'
                    : 'h-2 w-2 bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={onNext}
            disabled={activeIndex === doctors.length - 1}
            aria-label="Next"
            className="border-gray-150 flex h-12 w-12 cursor-pointer items-center justify-center rounded-full border bg-white text-slate-700 shadow-[0_2px_8px_rgba(0,0,0,0.08)] transition-all duration-200 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <svg
              className="text-text-main h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8.25 4.5l7.5 7.5-7.5 7.5"
              />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default SpecialistCarouselSlider;
